import { useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Save, Lock, AlertTriangle } from 'lucide-react';
import type { ProcessBlueprint, BlueprintStage } from '../../../../../types/processBlueprint';
import { BlueprintStageEditor } from '../../BlueprintStageEditor';
import { useBlueprintStageMutations } from '../../../hooks/useBlueprintStageMutations';

interface BlueprintStagesTabProps {
  blueprint: ProcessBlueprint;
  organizationId: string;
  onStagesUpdated?: () => void;
}

export function BlueprintStagesTab({ blueprint, organizationId, onStagesUpdated }: BlueprintStagesTabProps) {
  const [stages, setStages] = useState<BlueprintStage[]>(() => [...(blueprint.stages ?? [])]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const { updateStages, isPending, error } = useBlueprintStageMutations(organizationId);

  const isReadOnly = blueprint.status === 'published' || blueprint.status === 'standard';

  const handleStageChange = useCallback((index: number, updated: BlueprintStage) => {
    setStages(prev => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
    setDirty(true);
  }, []);

  const handleAddStage = useCallback(() => {
    const newStage: BlueprintStage = {
      order: stages.length + 1,
      name: `Etapa ${stages.length + 1}`,
      completionType: 'simple_complete',
    };
    setStages(prev => [...prev, newStage]);
    setExpandedIndex(stages.length);
    setDirty(true);
  }, [stages.length]);

  const handleRemoveStage = useCallback((index: number) => {
    setStages(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
    if (expandedIndex === index) setExpandedIndex(null);
    setDirty(true);
  }, [expandedIndex]);

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    setStages(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
    setExpandedIndex(index - 1);
    setDirty(true);
  }, []);

  const handleMoveDown = useCallback((index: number) => {
    if (index >= stages.length - 1) return;
    setStages(prev => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
    setExpandedIndex(index + 1);
    setDirty(true);
  }, [stages.length]);

  const handleSave = useCallback(async () => {
    try {
      await updateStages(blueprint.id, stages);
      setDirty(false);
      onStagesUpdated?.();
    } catch {
      // error is captured in the hook
    }
  }, [blueprint.id, stages, updateStages, onStagesUpdated]);

  return (
    <div className="p-4 space-y-4">
      {/* Banner de blueprint publicado */}
      {isReadOnly && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          <Lock size={14} className="text-amber-600 dark:text-amber-300 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Blueprint publicado âââ€šÂ¬â€Â crie uma nova versão para editar as etapas.
          </p>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <AlertTriangle size={14} className="text-red-600 dark:text-red-300 shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-400">{error.message}</p>
        </div>
      )}

      {/* Header com contagem */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
          Etapas do Processo ({stages.length})
        </h3>
        {!isReadOnly && (
          <button
            type="button"
            onClick={handleAddStage}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md border border-blue-200 dark:border-blue-800 transition-colors"
          >
            <Plus size={12} />
            Adicionar Etapa
          </button>
        )}
      </div>

      {/* Lista de stages como accordion */}
      {stages.length === 0 ? (
        <div className="text-center py-8 text-slate-400 dark:text-slate-500">
          <p className="text-sm">Nenhuma etapa definida</p>
          <p className="text-xs mt-1">Clique em "Adicionar Etapa" para começar</p>
        </div>
      ) : (
        <div className="space-y-2">
          {stages.map((stage, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <div
                key={`stage-${index}-${stage.sourceNodeId ?? stage.name}`}
                className="border border-slate-200 dark:border-slate-700 rounded overflow-hidden"
              >
                {/* Header do accordion */}
                <div
                  className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                    isExpanded
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-slate-200 dark:bg-slate-700 rounded text-[10px] font-bold text-slate-600 dark:text-slate-300 shrink-0">
                    {stage.order}
                  </span>
                  <span className="flex-1 text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                    {stage.name || 'Sem nome'}
                  </span>
                  {stage.role && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded">
                      {stage.role}
                    </span>
                  )}
                  {stage.slaHours && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                      {stage.slaHours}h
                    </span>
                  )}
                  {!isReadOnly && (
                    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 transition-colors"
                        title="Mover para cima"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === stages.length - 1}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 transition-colors"
                        title="Mover para baixo"
                      >
                        <ChevronDown size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveStage(index)}
                        className="p-1 text-slate-400 hover:text-red-600 transition-colors dark:text-slate-500 dark:hover:text-red-300"
                        title="Remover etapa"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                  <ChevronDown
                    size={14}
                    className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </div>

                {/* Conteúdo expandido */}
                {isExpanded && (
                  <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
                    <BlueprintStageEditor
                      stage={stage}
                      onChange={(updated) => handleStageChange(index, updated)}
                      readOnly={isReadOnly}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Botão salvar */}
      {!isReadOnly && dirty && (
        <div className="sticky bottom-0 pt-3 pb-1 bg-slate-50 dark:bg-[var(--bg-surface)]">
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded transition-colors"
          >
            <Save size={14} />
            {isPending ? 'Salvando...' : 'Salvar Etapas'}
          </button>
        </div>
      )}
    </div>
  );
}
